import { inject, Injectable } from '@angular/core';
import { Intercom as CapIntercom } from '@capacitor-community/intercom';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

import { DatadogLogsService } from '@ninety/mobile/platform/services';

import { ThemeUtil } from '../theme/theme.plugin';

/**
 * Persisted marker that an unidentified Intercom session exists. Lives in Capacitor `Preferences`
 * because the native SDK keeps the session across restarts but exposes no getter, so after a kill this
 * is the only record it exists. Set on anon-register; cleared on identified-register and any logout.
 */
const ANON_MARKER_KEY = 'intercom.anonymousSessionStarted';

/**
 * The single serialized owner of native Intercom session state: every native call goes through one
 * queue so they never interleave. The Capacitor bridge resolves before the SDK op finishes, so only
 * ordering is guaranteed — enough for the merge: anon activity merges into the identified user on a
 * later identify if no `logout()` runs between. The login path reads skip-logout from the marker, not
 * the LD flag (which can be true with no anon session, or flip false after one).
 */
@Injectable({ providedIn: 'root' })
export class IntercomNativeSessionService {
  private readonly logs = inject(DatadogLogsService);

  private queue: Promise<unknown> = Promise.resolve();

  /** Chain `op` after whatever is already queued, regardless of its outcome, so calls stay ordered. */
  private enqueue<T>(op: () => Promise<T>): Promise<T> {
    const run = this.queue.then(op, op);
    this.queue = run.catch(() => undefined);
    return run;
  }

  /**
   * Start or resume the anonymous launcher session for the login screen. Marker absent: `logout()`
   * first to clear a stale identified session (a prior user must not surface under the launcher on a
   * shared device), then register. Marker present: resume without a logout, which would orphan the
   * unidentified conversation unmergeably.
   */
  startAnonymousSession(): Promise<void> {
    return this.enqueue(async () => {
      try {
        const resuming = await this.hasAnonymousMarker();
        // Critical, not tolerated: the cleanup is the precondition for safely showing the launcher.
        // A swallowed failure would surface a prior user's session on a shared device.
        if (!resuming) await this.critical('logout', () => CapIntercom.logout());
        await this.setAnonymousMarker();
        await this.critical('registerUnidentifiedUser', () => CapIntercom.registerUnidentifiedUser());
        this.logs.info('Intercom anonymous session started', { resuming });
        await this.critical('displayLauncher', () => CapIntercom.displayLauncher());
        await this.refreshWindowInsets();
      } catch {
        // The failing step already logged; leave the marker for a later retry rather than orphaning.
      }
    });
  }

  /**
   * Identify the logged-in user, merging any anonymous activity into them. Skip-logout is read from
   * the marker: with an anon session we must not `logout()` (that orphans it and blocks the merge).
   * The marker clears once `registerIdentifiedUser` is issued but not if it fails, so a retry still
   * skips the logout and re-attempts the merge.
   */
  registerIdentified(userId: string | undefined, email: string | undefined, jwt: string): Promise<void> {
    return this.enqueue(async () => {
      const skipLogout = await this.hasAnonymousMarker();
      try {
        // Tolerated: pre-login cleanup, its failure doesn't invalidate the login that follows.
        if (!skipLogout) await this.tolerate('logout', () => CapIntercom.logout());
        // JWT before the user: user_id must match the JWT's user_id claim or it is rejected.
        await this.critical('setUserJwt', () => CapIntercom.setUserJwt({ jwt }));
        await this.critical('registerIdentifiedUser', () => CapIntercom.registerIdentifiedUser({ userId, email }));
        await this.clearAnonymousMarker();
        await this.critical('hideLauncher', () => CapIntercom.hideLauncher());
      } catch {
        // A failing step named itself and rethrew so the rest are skipped — registering a user whose
        // JWT never landed only produces a second, misleading error.
      }
    });
  }

  /**
   * Tear down on sign-out or Fin-token failure. Unconditional: always clears the marker and logs out,
   * so it never leaves a stale anon session a later login would skip isolation for. It enqueues before
   * the launcher's registration because `shutdownIntercom$` keys on the `notifyAppSignedOut` command.
   */
  logout(): Promise<void> {
    return this.enqueue(async () => {
      await this.clearAnonymousMarker();
      await this.tolerate('logout', () => CapIntercom.logout());
    });
  }

  /** Hide the launcher without touching the marker — the in-flight anon session still merges on login. */
  hideLauncher(): Promise<void> {
    return this.enqueue(() => this.tolerate('hideLauncher', () => CapIntercom.hideLauncher()));
  }

  private async refreshWindowInsets(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') return;
    await this.tolerate('refreshWindowInsets', () => ThemeUtil.refreshWindowInsets());
  }

  private async hasAnonymousMarker(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: ANON_MARKER_KEY });
      return value === 'true';
    } catch (error) {
      // Fail closed: an unreadable marker means the login path takes the isolation logout. That is
      // shared-device-safe but orphans an anon session if one existed.
      this.logs.info('Intercom anon marker read failed', undefined, error as Error);
      return false;
    }
  }

  private async setAnonymousMarker(): Promise<void> {
    try {
      await Preferences.set({ key: ANON_MARKER_KEY, value: 'true' });
    } catch (error) {
      this.logs.info('Intercom anon marker write failed', undefined, error as Error);
    }
  }

  private async clearAnonymousMarker(): Promise<void> {
    try {
      await Preferences.remove({ key: ANON_MARKER_KEY });
    } catch (error) {
      this.logs.info('Intercom anon marker clear failed', undefined, error as Error);
    }
  }

  /** A tolerated step: log its failure and continue. */
  private async tolerate(step: string, op: () => Promise<void>): Promise<void> {
    try {
      await op();
    } catch (error) {
      this.logs.info(`Intercom native ${step} failed`, undefined, error as Error);
    }
  }

  /** A step whose failure must skip the ones after it: log at error level and rethrow. */
  private async critical(step: string, op: () => Promise<void>): Promise<void> {
    try {
      await op();
    } catch (error) {
      this.logs.error(`Intercom native ${step} failed`, undefined, error as Error);
      throw error;
    }
  }
}
